import React, { useState, useEffect, useCallback, useContext } from "react";
import { TranslatorContext } from "../../../../context/Translator";
import { Link, useNavigate } from "react-router-dom";
import { Row, Col, Spinner, Alert, Table, Badge, Form, Pagination, Button } from "react-bootstrap";
import Select from "react-select";
import axios from "axios";
import PageLayout from "../../../../layouts/PageLayout";

export default function SellerProductIndex() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [products, setProducts] = useState([]);
    const [stores, setStores] = useState([]);

    const [currentPage, setCurrentPage] = useState(1);
    const [paginationMeta, setPaginationMeta] = useState(null);

    const [search, setSearch] = useState("");
    const [selectedStore, setSelectedStore] = useState("");

    const imgBaseUrl = process.env.REACT_APP_IMG_URL || "http://localhost:8000/storage/";

    const resolveImageUrl = (path) => {
        if (!path) return "";
        if (path.startsWith("http://") || path.startsWith("https://")) return path;
        return `${imgBaseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
    };

    const fetchSellerProducts = useCallback(async (page = 1) => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("authToken");
            const headers = { Authorization: `Bearer ${token}` };

            const params = {
                page,
                ...(search && { search }),
                ...(selectedStore && { store_id: selectedStore }),
            };

            const res = await axios.get(`${process.env.REACT_APP_API_URL}/admin/seller-products`, { headers, params });

            if (res.data?.success) {
                setProducts(res.data.data || []);
                if (res.data.stores) setStores(res.data.stores);
                setPaginationMeta(res.data.meta || null);
            }
        } catch (err) {
            console.error("Error fetching seller products:", err);
            setError(err.response?.data?.message || "Failed to fetch seller products.");
            setProducts([]);
        } finally {
            setLoading(false);
        }
    }, [search, selectedStore]);

    useEffect(() => {
        fetchSellerProducts(currentPage);
    }, [fetchSellerProducts, currentPage]);

    const storeOptions = stores.map((store) => ({
        value: String(store.id),
        label: store.name,
    }));

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb d-flex justify-content-between align-items-center">
                            <div>
                                <h3 className="mc-breadcrumb-title">
                                    {t("Seller Products")} ({paginationMeta?.total || products.length})
                                </h3>
                                <ul className="mc-breadcrumb-list">
                                    <li className="mc-breadcrumb-item"><Link to="#">{t("Home")}</Link></li>
                                    <li className="mc-breadcrumb-item">{t("Seller Products")}</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    <div className="mc-card">
                        {error && <Alert variant="danger" onClose={() => setError("")} dismissible>{error}</Alert>}

                        <Row className="g-3 align-items-end mb-4">
                            <Col sm={6} md={4} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold small text-uppercase text-muted mb-2">{t("Search")}</Form.Label>
                                    <Form.Control
                                        type="search"
                                        placeholder={t("Search Code, Title...")}
                                        value={search}
                                        onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                                    />
                                </Form.Group>
                            </Col>

                            <Col sm={6} md={4} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold small text-uppercase text-muted mb-2">{t("Filter by Store")}</Form.Label>
                                    <Select
                                        isClearable
                                        isSearchable
                                        placeholder={t("All Stores")}
                                        options={storeOptions}
                                        value={storeOptions.find(opt => opt.value === selectedStore) || null}
                                        onChange={(opt) => { setSelectedStore(opt ? opt.value : ""); setCurrentPage(1); }}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-2 text-muted">{t("Loading seller products...")}</p>
                            </div>
                        ) : (
                            <>
                                <div className="mc-table-responsive">
                                    <Table hover className="mc-table align-middle">
                                        <thead className="mc-table-head primary">
                                            <tr>
                                                <th>{t("Product")}</th>
                                                <th>{t("Code")}</th>
                                                <th>{t("Store")}</th>
                                                <th>{t("Selling Price")}</th>
                                                <th>{t("Action")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {products.length > 0 ? (
                                                products.map((product) => {
                                                    const imgPath = product.images?.[0]?.image || product.card_img;
                                                    return (
                                                        <tr key={product.id}>
                                                            <td>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    {imgPath && (
                                                                        <img
                                                                            src={resolveImageUrl(imgPath)}
                                                                            alt={product.title}
                                                                            style={{ width: "45px", height: "45px", objectFit: "cover", borderRadius: "6px" }}
                                                                        />
                                                                    )}
                                                                    <span className="fw-semibold text-truncate" style={{ maxWidth: "250px" }}>
                                                                        {product.title}
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td>{product.product_code}</td>
                                                            <td>{product.store?.name || "N/A"}</td>
                                                            <td className="fw-bold">${product.sell_price_dollar || "0.00"}</td>
                                                            <td>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <Button
                                                                        variant="primary"
                                                                        size="sm"
                                                                        onClick={() => navigate(`/admin/seller-products/detail`, { state: { product } })}
                                                                    >
                                                                        {t("View Details")}
                                                                    </Button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan={5} className="text-center py-4">{t("No seller products found.")}</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>

                                {paginationMeta && paginationMeta.last_page > 1 && (
                                    <div className="d-flex justify-content-between align-items-center mt-4">
                                        <span className="text-muted small">
                                            Showing {paginationMeta.from} to {paginationMeta.to} of {paginationMeta.total} results
                                        </span>
                                        <Pagination className="mb-0">
                                            <Pagination.Prev disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} />
                                            <Pagination.Item active>{currentPage}</Pagination.Item>
                                            <Pagination.Next disabled={currentPage === paginationMeta.last_page} onClick={() => setCurrentPage(p => p + 1)} />
                                        </Pagination>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </Col>
            </Row>
        </PageLayout>
    );
}